/**
 * ProductionDock — ปุ่มลอย "ติดตามการตรวจ" ข้างปุ่มงานของฉัน/แชท อยู่ทุกหน้า (16 ต.ค. 2569)
 *
 * ผู้ใช้: "ต้องการป๊อปอัพที่อยู่ในหน้าเว็บ คล้าย ๆ แชทหรืองานที่กำลังแสดงอยู่ เพื่อติดตามขั้นตอน QC หรือผลิต
 *          ตอนนี้กว่าจะเข้าไปดูในรอบผลิตได้ต้องหลายขั้นตอนเกินไป บางทีหัวหน้างานต้องการดูแค่ว่าตรวจไปถึงไหนแล้ว ทำอะไรไปบ้าง"
 *
 *   กด = หน้าต่างเล็ก: รอบผลิตที่ปักไว้ ทีละกล่อง — ตรวจแล้วกี่บอร์ด · ผ่าน/ไม่ผ่าน/ไม่ครบ · 5 บอร์ดล่าสุด ใครตรวจ เมื่อไร
 *   เลือกเองว่าจะปักรอบไหน (จำไว้ในเครื่องของคนนั้น) · ยังไม่ได้ปัก = เปิดมาเป็นหน้าเลือกงาน
 *   มีผลใหม่ระหว่างปิดหน้าต่าง = จุดบนปุ่ม (แดงถ้ามีบอร์ดไม่ผ่านใหม่)
 *   เห็นเฉพาะคนที่เปิดหน้าใบตรวจได้อยู่แล้ว (stock.request / stock.issue / แอดมิน) — ข้อมูล GET /api/test-plans/overview
 * ทางลัดเพิ่ม (16 ต.ค. 69 — "เพิ่มทางลัดหน้าที่แสดงว่างานไหนกำลังผลิตอยู่ และประวัติจ่ายออก ประวัติรับเข้า"):
 *   แท็บ "กำลังผลิต" = รอบผลิตที่เปิดอยู่ทั้งหมด (โปรเจกต์ขึ้นก่อน) · ปุ่มท้ายหน้าต่าง = ประวัติจ่ายออก / ประวัติรับเข้า (ตามสิทธิ์)
 * ⚠ ตำแหน่ง: ถัดจากปุ่ม "งานของฉัน" (right 4.75rem) ไปทางซ้าย · บนมือถือหน้าต่างเต็มความกว้าง
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Factory, X } from "lucide-react";
import { usePermission } from "../../hooks/usePermission";
import { HeaderSlotPortal, HEADER_PANEL_POS, headerIconBtn } from "../ui/HeaderSlot";

const PIN_KEY = (uid) => `prodDock.pins:${uid}`;
const readPins = (uid) => { try { const v = JSON.parse(localStorage.getItem(PIN_KEY(uid)) || "[]"); return Array.isArray(v) ? v.map(Number).filter(Boolean) : []; } catch { return []; } };
const writePins = (uid, p) => { try { localStorage.setItem(PIN_KEY(uid), JSON.stringify(p)); } catch { /* ไม่มีที่เก็บ = จำไม่ได้ ไม่เป็นไร */ } };
const STATUS = { PASS: ["ผ่าน", "text-emerald-800"], FAIL: ["ไม่ผ่าน", "text-rose-700"], PARTIAL: ["ตรวจไม่ครบ", "text-amber-800"] };
const ago = (v) => {
  if (!v) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(v)) / 60000));
  return m < 1 ? "เมื่อครู่" : m < 60 ? `${m} นาทีที่แล้ว` : m < 1440 ? `${Math.floor(m / 60)} ชม.ที่แล้ว` : `${Math.floor(m / 1440)} วันที่แล้ว`;
};
const KIND = { PROJECT: "โปรเจกต์", INTERNAL: "ใช้ภายใน", RND: "R&D", SALE: "ขาย", REPAIR: "ซ่อม" };
const btn = "inline-flex min-h-[2.5rem] items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-center text-[15px] font-bold text-slate-800 no-underline hover:bg-slate-50";

export default function ProductionDock() {
  const { userInfo } = useSelector((s) => s.auth);
  const { pathname } = useLocation();
  const { hasPermission } = usePermission();
  const uid = userInfo?._id;
  const allowed = Boolean(uid) && hasPermission(["stock.request", "stock.issue", "qc.test"]); // qc.test = พนักงาน QC (migration 129)
  const hidden = !allowed || pathname.startsWith("/login") || pathname.startsWith("/mini/");

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("track");        // track = ติดตามการตรวจ · live = กำลังผลิต
  const [showAll, setShowAll] = useState(false);  // กำลังผลิต: เฉพาะโปรเจกต์ / ทุกประเภท
  const [picking, setPicking] = useState(false);
  const [pins, setPins] = useState(() => (uid ? readPins(uid) : []));
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [news, setNews] = useState(null);          // มีผลใหม่ตอนหน้าต่างปิด: "FAIL" | "NEW" | null
  const seen = useRef(new Map());

  useEffect(() => { if (uid) setPins(readPins(uid)); }, [uid]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/test-plans/overview?runs=${pins.join(",")}${open ? "&open=1" : ""}`, { credentials: "include" });
      const body = await res.json().catch(() => null);
      if (!res.ok) { setErr(body?.message || `โหลดไม่สำเร็จ (${res.status})`); return; }
      setErr(null);
      let fresh = null;
      for (const r of body.runs || []) {
        const sig = `${r.tested}|${r.last?.board_no}|${r.last?.status}|${r.last?.at}`;
        const old = seen.current.get(r.id);
        if (old !== undefined && old !== sig) fresh = fresh === "FAIL" || r.last?.status === "FAIL" ? "FAIL" : "NEW";
        seen.current.set(r.id, sig);
      }
      if (fresh) setNews((n) => (n === "FAIL" ? n : fresh));
      setData(body);
    } catch (e) {
      setErr(e.message);
    }
  }, [pins, open]);

  useEffect(() => {
    if (hidden) return undefined;
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, open ? 15000 : 60000);
    return () => clearInterval(t);
  }, [hidden, open, load]);

  if (hidden) return null;

  const togglePin = (id) => {
    const next = pins.includes(id) ? pins.filter((x) => x !== id) : [...pins, id].slice(0, 8);
    setPins(next);
    writePins(uid, next);
  };
  const runs = data?.runs || [];
  const showPicker = picking || (data && !runs.length);
  const liveRuns = (data?.open || []).filter((r) => showAll || r.job_type === "PROJECT");
  const canIssue = hasPermission("stock.issue");
  const canReceive = hasPermission("stock.receive");

  return (
    <>
      {/* ปุ่มบนแถบหัวเว็บ (เดิมเป็นปุ่มลอยมุมขวาล่าง · ย้าย 20 ต.ค. 69) */}
      <HeaderSlotPortal>
        <button type="button" onClick={() => { setOpen((v) => !v); setNews(null); }} aria-pressed={open} aria-label="ติดตามการตรวจรอบผลิต" title="ติดตามการตรวจรอบผลิต"
          className={`${headerIconBtn} order-1 ${open ? "bg-slate-800 text-white border-slate-800" : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200"} ${news === "FAIL" ? "ring-2 ring-rose-500 animate-pulse" : ""}`}>
          <Factory className="w-4 h-4" />
          {news && <span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full ring-1 ring-white ${news === "FAIL" ? "bg-rose-600" : "bg-emerald-500"}`} />}
        </button>
      </HeaderSlotPortal>

      {open && (
        <section role="dialog" aria-label="ติดตามการตรวจรอบผลิต"
          className={`${HEADER_PANEL_POS} flex w-[min(26rem,calc(100vw-1.5rem))] max-h-[min(36rem,calc(100dvh-6rem))] flex-col overflow-hidden rounded-md border border-slate-300 bg-white text-[15px] text-slate-900 shadow-2xl`}>
          <header className="flex items-center gap-2 border-b border-slate-300 bg-slate-50 px-3 py-2">
            <Factory className="h-5 w-5 text-slate-700" />
            <h2 className="mr-auto text-[17px] font-black">งานผลิต</h2>
            {tab === "track" && <button type="button" className={btn} onClick={() => setPicking((v) => !v)}>{showPicker && runs.length ? "เสร็จ" : "เลือกงาน"}</button>}
            <button type="button" className={`${btn} w-10 px-0`} onClick={() => setOpen(false)} aria-label="ปิด"><X className="h-5 w-5" /></button>
          </header>

          <div className="grid grid-cols-2 border-b border-slate-300" role="tablist" aria-label="มุมมอง">
            {[["track", "ติดตามการตรวจ"], ["live", `กำลังผลิต${data?.open ? ` (${data.open.filter((r) => r.job_type === "PROJECT").length})` : ""}`]].map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                className={`min-h-[2.75rem] px-2 text-[15px] font-bold ${tab === k ? "border-b-4 border-slate-800 bg-white text-slate-900" : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}>{label}</button>
            ))}
          </div>

          {tab === "live" && (
            <div className="flex-1 space-y-2 overflow-y-auto p-3">
              <label className="flex items-center gap-2 text-[13px] font-bold text-slate-700">
                <input type="checkbox" className="h-5 w-5" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                แสดงทุกประเภท (ใช้ภายใน · R&D · ขาย) — ปกติแสดงเฉพาะโปรเจกต์
              </label>
              {!data?.open && <p className="text-slate-700">กำลังโหลด…</p>}
              {data?.open && !liveRuns.length && <p className="text-slate-700">ไม่มีรอบผลิตที่เปิดอยู่</p>}
              <ul className="divide-y divide-slate-300 rounded-md border border-slate-300">
                {liveRuns.map((r) => (
                  <li key={r.id}>
                    <Link to={`/production/runs/${r.id}`} onClick={() => setOpen(false)} className="block px-3 py-2 text-slate-900 no-underline hover:bg-slate-50">
                      <div className="break-words font-bold">{r.doc_no}{r.title ? ` · ${r.title}` : ""}</div>
                      <div className="text-[13px] text-slate-700">
                        {KIND[r.job_type] || r.job_type}{r.run_kind === "PROTOTYPE" ? " · ต้นแบบ" : r.run_kind === "PRODUCTION" ? " · ผลิตจริง" : ""}
                        {r.board_qty ? ` · ${r.board_qty} ชุด` : ""}{Number(r.tested) > 0 ? ` · ตรวจแล้ว ${r.tested}` : ""}
                        {r.created_name ? ` · เปิดโดย ${r.created_name}` : ""} · {ago(r.created_at)}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tab === "track" && (
          <div className="flex-1 space-y-2 overflow-y-auto p-3">
            {err && <p className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 font-bold text-rose-900">{err}</p>}
            {!data && !err && <p className="text-slate-700">กำลังโหลด…</p>}

            {data && showPicker && (
              <>
                <p className="text-[13px] text-slate-700">กดเพื่อปัก / เลิกปัก — รอบผลิตที่เปิดอยู่และมีใบตรวจ (ตัวเลข = ตรวจแล้ว / ทั้งหมด)</p>
                {(data.candidates || []).map((c) => {
                  const on = pins.includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => togglePin(c.id)}
                      className={`flex w-full min-h-[2.75rem] items-center gap-2 rounded-md border px-3 py-2 text-left font-bold ${on ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"}`}>
                      <span className="w-5 shrink-0">{on ? "✓" : ""}</span>
                      <span className="min-w-0 flex-1 break-words">{c.doc_no}{c.title ? ` · ${c.title}` : ""}</span>
                      <span className="shrink-0 font-normal tabular-nums text-slate-700">{c.tested}/{c.board_qty || 0}</span>
                    </button>
                  );
                })}
                {!(data.candidates || []).length && <p className="text-slate-700">ยังไม่มีรอบผลิตที่เปิดอยู่และมีใบตรวจ</p>}
              </>
            )}

            {data && !showPicker && runs.map((r) => {
              const pct = r.total ? Math.min(100, Math.round((r.tested / r.total) * 100)) : 0;
              return (
                <article key={r.id} className="space-y-2 rounded-md border border-slate-300 p-3">
                  <div className="break-words font-black">{r.doc_no}{r.title ? ` · ${r.title}` : ""}</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[22px] font-black tabular-nums">{r.tested}</span>
                    <span className="text-slate-700">/ {r.total} บอร์ด ตรวจแล้ว ({pct}%)</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded bg-slate-200" aria-hidden="true">
                    <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 font-bold">
                    <span className="text-emerald-800">ผ่าน {r.pass}</span>
                    <span className={r.fail ? "text-rose-700" : "text-slate-600"}>ไม่ผ่าน {r.fail}</span>
                    <span className={r.partial ? "text-amber-800" : "text-slate-600"}>ตรวจไม่ครบ {r.partial}</span>
                    <span className="text-slate-600">ยังไม่ตรวจ {Math.max(0, r.total - r.tested)}</span>
                  </div>
                  {r.recent?.length > 0 && (
                    <div>
                      <div className="text-[13px] font-bold text-slate-700">ล่าสุด</div>
                      <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
                        {r.recent.map((x) => (
                          <li key={`${x.board_no}-${x.at}`} className="flex flex-wrap items-baseline gap-x-2 px-2 py-1 text-[13px]">
                            <span className="font-bold">บอร์ด {x.board_no}</span>
                            <span className={`font-bold ${STATUS[x.status]?.[1] || ""}`}>{STATUS[x.status]?.[0] || x.status}</span>
                            <span className="text-slate-700">{x.tester_name || "-"} · {ago(x.at)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Link to={`/production/runs/${r.id}/test`} onClick={() => setOpen(false)} className={btn}>เปิดใบตรวจ</Link>
                    <Link to={`/production/runs/${r.id}`} onClick={() => setOpen(false)} className={btn}>เปิดรอบผลิต</Link>
                  </div>
                </article>
              );
            })}
          </div>
          )}

          {(canIssue || canReceive) && (
            <div className="grid grid-cols-2 gap-2 border-t border-slate-300 bg-slate-50 p-2">
              {canIssue && <Link to="/componentissuelist" onClick={() => setOpen(false)} className={btn}>ประวัติจ่ายออก</Link>}
              {canReceive && <Link to="/componentreceivelist" onClick={() => setOpen(false)} className={btn}>ประวัติรับเข้า</Link>}
            </div>
          )}
        </section>
      )}
    </>
  );
}
